import { Add, Remove } from "@mui/icons-material";
import {
  Box,
  Chip,
  Dropdown,
  FormHelperText,
  FormLabel,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  Stack,
  Typography,
} from "@mui/joy";
import { ClimbType } from "griptonite-api-client";
import { useMemo } from "react";
import { useController, useFormContext } from "react-hook-form";

import { useGradesQuery } from "../../hooks/grades";
import { useCurrentClimbType, useCurrentVenueQuery } from "../../hooks/venues";
import {
  convertBetweenSystems,
  getAllGradeValuesForSystem,
  getGradeSystemsForClimbType,
} from "../../utils/grades";
import FormGroup from "./FormGroup";
import MultipleValuesSelectedIndicator from "./MultipleValuesSelected";

type GradeChangeEvent = { gradeValue: string | null; gradeSystem?: string };

type Props = {
  gradeSystem: string;
  gradeValue: string | null;
  gradeSystemChoices: string[];
  onChange: (change: GradeChangeEvent) => void;
};

export const GradeSystemDropdown = ({
  gradeSystem,
  choices,
  onChange,
}: {
  gradeSystem: string;
  choices: string[];
  onChange: (gradeSystem: string) => void;
}) => {
  return (
    <Dropdown>
      <MenuButton slots={{ root: Chip }} slotProps={{ root: { variant: "outlined" } }}>
        {gradeSystem}
      </MenuButton>
      <Menu>
        {choices.map((choice) => (
          <MenuItem key={choice} onClick={() => onChange(choice)} selected={choice === gradeSystem}>
            {choice}
          </MenuItem>
        ))}
      </Menu>
    </Dropdown>
  );
};

export default function GradePicker({
  gradeSystem,
  gradeValue,
  gradeSystemChoices,
  onChange,
}: Props) {
  const { data: gradesData } = useGradesQuery();

  const { prevGrade, nextGrade } = useMemo(() => {
    if (!gradesData || !gradeSystem) {
      return { prevGrade: undefined, nextGrade: undefined };
    }
    const gradeValues = [null, ...getAllGradeValuesForSystem(gradeSystem)];
    // if (!gradeValue) {
    //   return { prevGrade: null, nextGrade: gradeValues[0] };
    // }
    const gradeIndex = gradeValues.indexOf(gradeValue);
    const prevGrade = gradeValues[gradeIndex - 1];
    const nextGrade = gradeValues[gradeIndex + 1];
    return { prevGrade, nextGrade };
  }, [gradeSystem, gradeValue, gradesData]);

  const onChangeGradeSystem = (newGradeSystem: string) => {
    // Convert the grade value to the nearest value in the new system
    const newGradeValue = convertBetweenSystems(gradeValue, gradeSystem, newGradeSystem);
    onChange({ gradeValue: newGradeValue, gradeSystem: newGradeSystem });
  };

  return (
    <Box>
      <Stack direction="row" spacing={1} justifyContent="center" sx={{ marginBottom: 1 }}>
        <IconButton
          disabled={prevGrade === undefined}
          onClick={() => prevGrade !== undefined && onChange({ gradeValue: prevGrade })}
          variant="soft"
          sx={{ borderRadius: 100, cursor: "pointer" }}
        >
          <Remove />
        </IconButton>

        <Typography
          sx={{
            width: 100,
            textAlign: "center",
            fontSize: 24,
            fontWeight: "bold",
          }}
        >
          {gradeValue === null ? "?" : gradeValue}
        </Typography>

        <IconButton
          disabled={nextGrade === undefined}
          onClick={() => nextGrade !== undefined && onChange({ gradeValue: nextGrade })}
          variant="soft"
          sx={{ borderRadius: 100, cursor: "pointer" }}
        >
          <Add />
        </IconButton>
      </Stack>
      <Box
        sx={{
          justifyContent: "center",
          display: "flex",
        }}
      >
        <GradeSystemDropdown
          gradeSystem={gradeSystem}
          choices={gradeSystemChoices}
          onChange={onChangeGradeSystem}
        />
      </Box>
    </Box>
  );
}

const emptyArray: never[] = [];

export const GradePickerFormInput = ({
  name,
  gradeSystemName,
  label,
  climbType,
}: {
  name: string;
  gradeSystemName?: string;
  label: string;
  climbType?: ClimbType;
}) => {
  // const { values, setFieldValue } = useFormikContext<{
  //   gradeSystem: string;
  //   gradeValue: string | null;
  // }>();
  // const { gradeSystem, gradeValue } = values;
  const { control, setValue } = useFormContext();
  const { field: gradeSystemField } = useController({
    name: gradeSystemName || "gradeSystem",
    control,
  });
  const { field: gradeValueField, fieldState: gradeValueFieldState } = useController({
    name,
    control,
  });

  const { data: venue } = useCurrentVenueQuery();
  const currentClimbType = useCurrentClimbType();
  climbType = climbType || currentClimbType || venue?.climbTypes[0] || "BOULDER";

  const extraSystems = venue
    ? climbType === "BOULDER"
      ? venue?.boulderGradeSystems
      : venue?.sportGradeSystems
    : emptyArray;

  const choices = useMemo(
    () => [...new Set(getGradeSystemsForClimbType(climbType).concat(extraSystems))],
    [climbType, extraSystems],
  );

  return (
    <FormGroup error={gradeValueFieldState.isTouched && Boolean(gradeValueFieldState.error)}>
      <FormLabel>{label}</FormLabel>
      <MultipleValuesSelectedIndicator name={name} />
      <GradePicker
        gradeSystem={gradeSystemField.value}
        gradeValue={gradeValueField.value}
        gradeSystemChoices={choices}
        onChange={(updatedValues) => {
          gradeSystemField.onChange(updatedValues.gradeSystem || gradeSystemField.value);
          gradeValueField.onChange(updatedValues.gradeValue);
          setValue("gradeEstimated", false);
        }}
      />
      <FormHelperText>{gradeValueFieldState.error?.message}</FormHelperText>
    </FormGroup>
  );
};
